import { useEffect, useState } from 'react';
import { fetchEdgeConfig, fetchEdgeConfigBatch } from '@/lib/edge-config/client';

/**
 * Custom hook for fetching Edge Config values
 * 
 * @param {string|string[]} keyOrKeys - Single key or array of keys to fetch
 * @param {Object} options - Hook options
 * @param {any} options.defaultValue - Default value to use while loading or on error
 * @param {boolean} options.useCache - Whether to use cache (default: true)
 * @returns {[any, boolean, Error|null]} - [value, isLoading, error]
 * 
 * @example
 * // Single key
 * const [ctaType, isLoading] = useEdgeConfig('blog_cta_type');
 * 
 * @example
 * // Multiple keys
 * const [{ bannerText, bannerCta }, isLoading] = useEdgeConfig(['top_banner_text', 'top_banner_cta']);
 * 
 * @example
 * // With default value
 * const [showBanner, isLoading] = useEdgeConfig('top_banner_display', { defaultValue: false });
 */
export function useEdgeConfig(keyOrKeys, { defaultValue = null, useCache = true } = {}) {
    const isMultiple = Array.isArray(keyOrKeys);
    const requestKey = JSON.stringify(keyOrKeys);
    
    const [values, setValues] = useState(() => 
        isMultiple ? {} : defaultValue
    );
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const requested = JSON.parse(requestKey);

        async function fetchConfig() {
            try {
                setIsLoading(true);
                setError(null);

                if (Array.isArray(requested)) {
                    const results = await fetchEdgeConfigBatch(requested, { useCache });
                    if (isMounted) {
                        setValues(results);
                    }
                } else {
                    const value = await fetchEdgeConfig(requested, { useCache });
                    if (isMounted) {
                        setValues(value ?? defaultValue);
                    }
                }
            } catch (err) {
                console.error('useEdgeConfig error:', err);
                if (isMounted) {
                    setError(err);
                    if (Array.isArray(requested)) {
                        setValues({});
                    } else {
                        setValues(defaultValue);
                    }
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        }

        fetchConfig();

        return () => {
            isMounted = false;
        };
    }, [requestKey, defaultValue, useCache]);

    return [values, isLoading, error];
}
